import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { enumLabel } from '../shared/i18n/enumLabel';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { StatusBadge, statusToneFromEnum } from '../shared/ui/StatusBadge';

export function InvoicesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canRead = useAuthStore((s) => s.hasPermission('billing:read'));
  const canWrite = useAuthStore((s) => s.hasPermission('billing:write'));
  const [filter, setFilter] = useState<string>('ALL');

  const invoices = useQuery({
    queryKey: ['invoices', filter],
    queryFn: () => api.listInvoices(filter === 'ALL' ? undefined : filter),
    enabled: canRead,
  });

  const series = useQuery({
    queryKey: ['invoice-series'],
    queryFn: () => api.listInvoiceSeries(),
    enabled: canRead,
  });

  const voidInvoice = useMutation({
    mutationFn: (id: string) => api.voidInvoice(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  if (!canRead) {
    return <div className="alert alert-warning">{t('invoices.forbidden')}</div>;
  }

  const rows = invoices.data?.data ?? [];
  const columns = [
    t('invoices.number'),
    t('invoices.plate'),
    t('invoices.customer'),
    t('invoices.status'),
    t('invoices.net'),
    t('invoices.vat'),
    t('invoices.total'),
    t('invoices.verifactu'),
    t('invoices.issuedAt'),
    ...(canWrite ? [t('common.actions')] : []),
  ];

  return (
    <>
{(series.data?.data?.length ?? 0) > 0 && (
        <div className="alert alert-info">
          {t('invoices.seriesHint', {
            series: series.data!.data.map((s) => `${s.code} (next ${s.nextNumber})`).join(' · '),
          })}
        </div>
      )}

      <div className="mb-4" style={{ maxWidth: 220 }}>
        <label className="form-label" htmlFor="inv-filter">
          {t('invoices.filter')}
        </label>
        <select
          id="inv-filter"
          className="form-select"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="ALL">{t('invoices.filterAll')}</option>
          <option value="PAID">{enumLabel(t, 'invoiceStatus', 'PAID')}</option>
          <option value="ISSUED">{enumLabel(t, 'invoiceStatus', 'ISSUED')}</option>
          <option value="VOID">{enumLabel(t, 'invoiceStatus', 'VOID')}</option>
        </select>
      </div>

      {invoices.isError && (
        <div className="alert alert-danger">{(invoices.error as Error).message}</div>
      )}

      <Card>
        <ParkingDataTable
          storageKey="invoices"
          columns={columns}
          refreshKey={`${filter}|${rows.map((i) => `${i.id}:${i.status}`).join('|')}`}
          order={[[8, 'desc']]}
          actionsColumn={canWrite}
          galleryItems={rows.map((inv) => ({
            id: inv.id,
            icon: 'bi-receipt',
            iconTone: statusToneFromEnum(inv.status),
            title: inv.fullNumber,
            subtitle: `${inv.plate ?? '—'} · ${inv.customerName ?? '—'}`,
            meta: `${(inv.totalCents / 100).toFixed(2)} € · ${inv.issuedAt ? new Date(inv.issuedAt).toLocaleString() : '—'}`,
            badge: (
              <StatusBadge tone={statusToneFromEnum(inv.status)}>
                {enumLabel(t, 'invoiceStatus', inv.status)}
              </StatusBadge>
            ),
            actions:
              canWrite && inv.status !== 'VOID' ? (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => {
                    if (window.confirm(t('invoices.confirmVoid'))) {
                      voidInvoice.mutate(inv.id);
                    }
                  }}
                >
                  {t('invoices.void')}
                </button>
              ) : undefined,
          }))}
        >
          {rows.map((inv) => (
            <tr key={inv.id}>
              <td className="fw-semibold">{inv.fullNumber}</td>
              <td>{inv.plate ?? '—'}</td>
              <td>{inv.customerName ?? '—'}</td>
              <td>
                <StatusBadge tone={statusToneFromEnum(inv.status)}>
                  {enumLabel(t, 'invoiceStatus', inv.status)}
                </StatusBadge>
              </td>
              <td>{(inv.netCents / 100).toFixed(2)} €</td>
              <td>
                {(inv.vatCents / 100).toFixed(2)} € ({inv.vatRate}%)
              </td>
              <td>{(inv.totalCents / 100).toFixed(2)} €</td>
              <td>
                <StatusBadge tone={inv.verifactuReady ? 'info' : 'secondary'}>
                  {inv.verifactuReady ? 'OK' : '—'}
                </StatusBadge>
              </td>
              <td>{inv.issuedAt ? new Date(inv.issuedAt).toLocaleString() : '—'}</td>
              {canWrite && (
                <td>
                  {inv.status !== 'VOID' && (
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => {
                        if (window.confirm(t('invoices.confirmVoid'))) {
                          voidInvoice.mutate(inv.id);
                        }
                      }}
                    >
                      {t('invoices.void')}
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </ParkingDataTable>
      </Card>
    </>
  );
}
